/**
 * Admin console login — email + password → useAdminSession.login(). The store returns the next
 * stage ('mfa_required' | 'enroll_required'); the AdminAuthGate routes accordingly, but we also
 * push directly for snappier UX. Styled to match app/login.tsx.
 */
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useAdminSession } from '../../src/auth/adminSession';
import { AdminApiError } from '../../src/api/adminClient';
import { Screen, Text, Field, Button } from '../../src/components/ui';
import { theme } from '../../src/theme';

export default function AdminLoginScreen() {
  const login = useAdminSession((s) => s.login);
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin() {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Email and password are required.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const stage = await login(trimmedEmail, password);
      if (stage === 'authenticated') {
        router.replace('/admin'); // trusted device — TOTP skipped
      } else if (stage === 'enroll_required') {
        router.replace('/admin/enroll');
      } else {
        router.replace('/admin/verify-mfa');
      }
    } catch (err) {
      if (err instanceof AdminApiError || err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Screen maxWidth={420}>
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <View style={styles.container}>
          <View style={styles.brand}>
            <Text variant="title">BEAM platform admin</Text>
            <Text variant="subhead" color="secondary">
              Sign in to the console
            </Text>
          </View>

          <View style={styles.form}>
            <Field
              label="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              textContentType="emailAddress"
              autoComplete="email"
              placeholder="admin@beam.app"
              editable={!loading}
              accessibilityLabel="Admin email address"
              returnKeyType="next"
            />

            <Field
              label="Password"
              value={password}
              onChangeText={setPassword}
              secureToggle
              textContentType="password"
              autoComplete="password"
              placeholder="Password"
              editable={!loading}
              accessibilityLabel="Admin password"
              returnKeyType="done"
              onSubmitEditing={handleLogin}
              error={error ?? undefined}
            />

            <Button
              title="Sign in"
              onPress={handleLogin}
              loading={loading}
              disabled={loading}
              size="lg"
              style={styles.submit}
            />
          </View>
        </View>
      </KeyboardAvoidingView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  container: { flex: 1, justifyContent: 'center', gap: theme.spacing['4xl'] },
  brand: { alignItems: 'center', gap: theme.spacing.md },
  form: { gap: theme.spacing.md },
  submit: { marginTop: theme.spacing.sm },
});
